<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <div>
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-gray-200">
          API Keys
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mt-1">
          Manage your API keys for accessing KappaML services
        </p>
      </div>
      <button
        class="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
        @click="showCreateModal = true"
      >
        <Icon :path="mdiPlus" class="w-5 h-5" />
        Add key
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"
      ></div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 text-red-600 dark:text-red-400"
    >
      {{ error }}
    </div>

    <!-- Data Table -->
    <div
      v-else
      class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
    >
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Name
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Created
            </th>
            <!-- <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Last Used
            </th> -->
            <th
              class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody
          class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700"
        >
          <tr v-if="!apiKeys.length" class="text-center">
            <td colspan="4" class="px-6 py-8 text-gray-500 dark:text-gray-400">
              No API keys found. Add a key to get started.
            </td>
          </tr>
          <tr
            v-for="key in apiKeys"
            :key="key.id"
            class="hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="font-medium text-gray-900 dark:text-gray-200">
                {{ key.name }}
              </div>
            </td>
            <td
              class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
            >
              {{ formatDate(key.created_at) }}
            </td>
            <!-- <td
              class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
            >
              {{ key.last_used_at ? formatDate(key.last_used_at) : "Never" }}
            </td> -->
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
              <button
                class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium flex items-center gap-1 ml-auto"
                @click="confirmDelete(key)"
              >
                <Icon :path="mdiDelete" class="w-4 h-4" />
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create API Key Modal -->
    <Modal v-model="showCreateModal" title="Create New API Key">
      <form @submit.prevent="createNewKey">
        <div class="mb-4">
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Key Name
          </label>
          <input
            v-model="newKeyName"
            type="text"
            required
            class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            placeholder="Enter a name for your API key"
          />
        </div>
      </form>
      <template #footer>
        <button
          type="button"
          class="px-4 py-2 text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          @click="showCreateModal = false"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          :disabled="isCreating"
          @click="createNewKey"
        >
          {{ isCreating ? "Creating..." : "Create Key" }}
        </button>
      </template>
    </Modal>

    <!-- New API Key Modal -->
    <Modal v-model="showNewKeyModal" title="Your New API Key">
      <template #header-right>
        <button class="text-primary hover:text-primary/80" @click="copyApiKey">
          <Icon :path="copied ? mdiCheck : mdiContentCopy" class="w-5 h-5" />
        </button>
      </template>
      <p class="text-sm text-red-600 dark:text-red-400 mb-4">
        Make sure to copy your API key now. You won't be able to see it again!
      </p>
      <div
        class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg font-mono text-sm break-all mb-6 text-gray-900 dark:text-gray-200"
      >
        {{ newApiKey }}
      </div>
      <template #footer>
        <button
          class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          @click="closeNewKeyModal"
        >
          Done
        </button>
      </template>
    </Modal>

    <!-- Delete Confirmation Modal -->
    <Modal v-model="showDeleteModal" title="Delete API Key">
      <p class="text-gray-600 dark:text-gray-400 mb-4">
        Are you sure you want to delete the API key "{{ keyToDelete?.name }}"?
        This action cannot be undone.
      </p>
      <template #footer>
        <button
          class="px-4 py-2 text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          @click="showDeleteModal = false"
        >
          Cancel
        </button>
        <button
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          :disabled="isDeleting"
          @click="deleteKey"
        >
          <Icon v-if="!isDeleting" :path="mdiDelete" class="w-4 h-4" />
          {{ isDeleting ? "Deleting..." : "Delete" }}
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import Icon from "@/components/Icon.vue";
import Modal from "@/components/shared/Modal.vue";
import { mdiPlus, mdiDelete, mdiContentCopy, mdiCheck } from "@mdi/js";
import kappamlAPI from "@/services/kappaml-api";

export default {
  name: "ApiKeyManager",
  components: {
    Icon,
    Modal,
  },
  setup() {
    const apiKeys = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const showCreateModal = ref(false);
    const showNewKeyModal = ref(false);
    const showDeleteModal = ref(false);
    const newKeyName = ref("");
    const newApiKey = ref("");
    const isCreating = ref(false);
    const isDeleting = ref(false);
    const keyToDelete = ref(null);
    const copied = ref(false);

    const fetchApiKeys = async () => {
      try {
        loading.value = true;
        error.value = null;
        apiKeys.value = await kappamlAPI.listApiKeys();
      } catch (err) {
        error.value = err.message;
      } finally {
        loading.value = false;
      }
    };

    const createNewKey = async () => {
      try {
        isCreating.value = true;
        const result = await kappamlAPI.createApiKey(newKeyName.value);
        newApiKey.value = result.key;
        showCreateModal.value = false;
        showNewKeyModal.value = true;
        await fetchApiKeys();
      } catch (err) {
        error.value = err.message;
      } finally {
        isCreating.value = false;
      }
    };

    const confirmDelete = (key) => {
      keyToDelete.value = key;
      showDeleteModal.value = true;
    };

    const deleteKey = async () => {
      if (!keyToDelete.value) return;

      try {
        isDeleting.value = true;
        await kappamlAPI.deleteApiKey(keyToDelete.value.id);
        showDeleteModal.value = false;
        await fetchApiKeys();
      } catch (err) {
        error.value = err.message;
      } finally {
        isDeleting.value = false;
        keyToDelete.value = null;
      }
    };

    const copyApiKey = async () => {
      try {
        await navigator.clipboard.writeText(newApiKey.value);
        copied.value = true;
        setTimeout(() => {
          copied.value = false;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy API key:", err);
      }
    };

    const closeNewKeyModal = () => {
      showNewKeyModal.value = false;
      newKeyName.value = "";
      newApiKey.value = "";
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleString();
    };

    onMounted(fetchApiKeys);

    return {
      apiKeys,
      loading,
      error,
      showCreateModal,
      showNewKeyModal,
      showDeleteModal,
      newKeyName,
      newApiKey,
      isCreating,
      isDeleting,
      keyToDelete,
      copied,
      createNewKey,
      confirmDelete,
      deleteKey,
      copyApiKey,
      closeNewKeyModal,
      formatDate,
      mdiPlus,
      mdiDelete,
      mdiContentCopy,
      mdiCheck,
    };
  },
};
</script>

<style scoped></style>
